import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  TrendingUp, TrendingDown, DollarSign, PlusCircle, Trash2,
  Tag, X, BarChart2, List, ChevronDown, Pencil, Check, AlertCircle
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { FinanceRowsSkeleton } from "../components/ui/Skeleton";
import { Button } from "../components/ui/Button";
import { Input, Label, Select } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Badge } from "../components/ui/Badge";
import { toast } from "../components/ui/Toast";
import { formatVND, formatDate, formatDateTime, parseApiError} from "../lib/utils";
import { cn } from "../lib/utils";

// ── Màu mặc định cho danh mục ────────────────────────────────────────────────
const PRESET_COLORS = [
  "#2D4A22", "#5B8A3C", "#E07B39", "#C0392B", "#2980B9",
  "#8E44AD", "#16A085", "#D35400", "#F39C12", "#7F8C8D",
];

const PERIOD_OPTS = [
  { value: "daily",   label: "Hôm nay" },
  { value: "weekly",  label: "Tuần này" },
  { value: "monthly", label: "Tháng này" },
  { value: "yearly",  label: "Năm này" },
];

// ── Hook fetch dữ liệu Finance ─────────────────────────────────────────────────
function useFinanceData(period, dateFrom, dateTo) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to   = dateTo;
      } else {
        params.period = period;
      }
      const res = await api.get("/finance/transactions", { params });
      setData(res.data);
    } catch {
      toast({ title: "Không thể tải dữ liệu thu chi", variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [period, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

// ── Hook fetch danh mục ────────────────────────────────────────────────────────
function useCategories() {
  const [cats, setCats] = useState([]);
  const load = useCallback(async () => {
    const res = await api.get("/finance/categories");
    setCats(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  return { cats, reload: load };
}

// ── Stat card ─────────────────────────────────────────────────────────────────
const STAT_THEMES = {
  income: {
    gradient: "from-emerald-600 to-teal-500",
    glow: "shadow-emerald-200",
    bar: "bg-white/60",
  },
  expense: {
    gradient: "from-red-600 to-rose-500",
    glow: "shadow-rose-200",
    bar: "bg-white/60",
  },
  profit_positive: {
    gradient: "from-amber-500 to-yellow-400",
    glow: "shadow-amber-200",
    bar: "bg-white/60",
  },
  gross_profit: {
    gradient: "from-[#7c3aed] to-[#a855f7]",
    glow:     "shadow-[0_8px_32px_rgba(124,58,237,0.30)]",
  },
  net_profit: {
    gradient: "from-sky-600 to-blue-500",
    glow:     "shadow-sky-200",
    bar:      "bg-white/60",
  },
  pure_profit: {
    gradient: "from-amber-500 to-orange-400",
    glow:     "shadow-amber-200",
    bar:      "bg-white/60",
  },
  profit_negative: {
    gradient: "from-slate-700 to-slate-600",
    glow: "shadow-slate-200",
    bar: "bg-white/40",
  },
};

function StatCard({ label, value, icon: Icon, color, sub, themeKey }) {
  const theme = STAT_THEMES[themeKey] || STAT_THEMES.income;
  return (
    <div className={cn(
      "relative overflow-hidden rounded-xl p-4 text-white shadow-md group hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 bg-gradient-to-br",
      theme.gradient, theme.glow
    )}>
      <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 70% 20%, white 0%, transparent 60%)'}} />
      <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full bg-white/10 group-hover:scale-110 transition-transform duration-500" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-white/70">{label}</span>
          <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <Icon size={14} className="text-white" />
          </div>
        </div>
        <div className="font-heading text-xl font-bold tracking-tight leading-none">{formatVND(value)}</div>
        {sub && (
          <div className="flex items-center gap-1 mt-2 text-white/80 text-[11px] font-medium">
            <span>{sub}</span>
          </div>
        )}
        <div className="mt-2 h-0.5 rounded-full bg-white/20">
          <div className={cn("h-0.5 rounded-full w-full", theme.bar)} />
        </div>
      </div>
    </div>
  );
}

// ── Modal thêm danh mục ────────────────────────────────────────────────────────
function CategoryModal({ open, onClose, onSaved, editItem }) {
  const [name, setName]       = useState("");
  const [type, setType]       = useState("expense");
  const [color, setColor]     = useState(PRESET_COLORS[0]);
  const [desc, setDesc]       = useState("");
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (editItem) {
      setName(editItem.name);
      setType(editItem.type);
      setColor(editItem.color || PRESET_COLORS[0]);
      setDesc(editItem.description || "");
    } else {
      setName(""); setType("expense"); setColor(PRESET_COLORS[0]); setDesc("");
    }
  }, [editItem, open]);

  const handleSave = async () => {
    if (!name.trim()) { toast({ title: "Vui lòng nhập tên danh mục", variant: "error" }); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), type, color, description: desc };
      if (editItem) {
        await api.put(`/finance/categories/${editItem.category_id}`, payload);
        toast({ title: "Đã cập nhật danh mục", variant: "success" });
      } else {
        await api.post("/finance/categories", payload);
        toast({ title: "Đã tạo danh mục", variant: "success" });
      }
      onSaved();
      onClose();
    } catch {
      toast({ title: "Lỗi khi lưu danh mục", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editItem ? "Sửa danh mục" : "Tạo danh mục mới"}>
      <div className="space-y-4 py-2">
        <div>
          <Label>Tên danh mục *</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="VD: Tiền điện, Nguyên liệu..." />
        </div>
        <div>
          <Label>Loại</Label>
          <Select value={type} onChange={e => setType(e.target.value)}>
            <option value="expense">Chi phí</option>
            <option value="income">Thu nhập</option>
          </Select>
        </div>
        <div>
          <Label>Màu nhận diện</Label>
          <div className="flex gap-2 flex-wrap mt-1">
            {PRESET_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)}
                className={cn("w-7 h-7 rounded-full border-2 transition-all", color === c ? "border-ink scale-110" : "border-transparent")}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
        <div>
          <Label>Ghi chú</Label>
          <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Mô tả (tuỳ chọn)" />
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="primary" onClick={handleSave} disabled={saving} className="flex-1">
            {saving ? "Đang lưu..." : (editItem ? "Cập nhật" : "Tạo danh mục")}
          </Button>
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Modal thêm giao dịch (Thu / Chi) ──────────────────────────────────────────
function TransactionModal({ open, onClose, onSaved, cats, defaultType = "expense" }) {
  const [type, setType]       = useState(defaultType);
  const [catId, setCatId]     = useState("");
  const [amount, setAmount]   = useState("");
  const [desc, setDesc]       = useState("");
  const [date, setDate]       = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving]   = useState(false);

  useEffect(() => { setType(defaultType); setCatId(""); setAmount(""); setDesc(""); }, [open, defaultType]);

  const filteredCats = useMemo(() => cats.filter(c => c.type === type), [cats, type]);

  const handleSave = async () => {
    if (!amount || Number(amount) <= 0) { toast({ title: "Vui lòng nhập số tiền hợp lệ", variant: "error" }); return; }
    setSaving(true);
    try {
      await api.post("/finance/transactions", {
        type, category_id: catId || null,
        amount: Number(amount), description: desc, date, source: "manual",
      });
      toast({ title: type === "income" ? "Đã ghi thu" : "Đã ghi chi", variant: "success" });
      onSaved();
      onClose();
    } catch {
      toast({ title: "Lỗi khi lưu giao dịch", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Thêm giao dịch">
      <div className="space-y-4 py-2">
        <div className="flex gap-2">
          <button onClick={() => setType("income")}
            className={cn("flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all", type === "income" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-border text-ink-muted hover:border-emerald-300")}>
            ↑ Thu tiền
          </button>
          <button onClick={() => setType("expense")}
            className={cn("flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all", type === "expense" ? "border-red-400 bg-red-50 text-red-700" : "border-border text-ink-muted hover:border-red-300")}>
            ↓ Chi tiền
          </button>
        </div>

        <div>
          <Label>Danh mục</Label>
          <Select value={catId} onChange={e => setCatId(e.target.value)}>
            <option value="">{type === "income" ? "— Thu khác —" : "— Chi khác —"}</option>
            {filteredCats.map(c => (
              <option key={c.category_id} value={c.category_id}>{c.name}</option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Số tiền (₫) *</Label>
          <Input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" />
        </div>
        <div>
          <Label>Ngày</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div>
          <Label>Diễn giải</Label>
          <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Ghi chú giao dịch..." />
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="primary" onClick={handleSave} disabled={saving} className="flex-1">
            {saving ? "Đang lưu..." : "Lưu giao dịch"}
          </Button>
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Pie chart wrapper ──────────────────────────────────────────────────────────
function BreakdownChart({ data, title, emptyText }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-ink-muted text-sm gap-2">
        <AlertCircle size={28} className="opacity-30" />
        <span>{emptyText}</span>
      </div>
    );
  }
  return (
    <div>
      <p className="text-sm font-semibold text-ink mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} dataKey="total" nameKey="category_name"
            cx="50%" cy="50%" outerRadius={90} innerRadius={48}
            paddingAngle={2} label={({ category_name, percent }) =>
              percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""
            } labelLine={false}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color || PRESET_COLORS[i % PRESET_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => formatVND(v)} />
          <Legend iconType="circle" iconSize={8} formatter={(v) => <span className="text-xs">{v}</span>} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}


// ── Modal chi tiết thu theo hình thức thanh toán ─────────────────────────────
function IncomeDetailModal({ open, onClose, data }) {
  const printRef = React.useRef();

  if (!open || !data) return null;

  const methods = data.income_by_method || {};
  const total = data.total_income || 0;

  const METHOD_CONFIG = {
    "Tiền mặt":      { icon: "💵", color: "#16A085", bg: "#e8f8f5" },
    "Chuyển khoản":  { icon: "🏦", color: "#2980B9", bg: "#eaf4fb" },
    "Ví điện tử":    { icon: "📱", color: "#8E44AD", bg: "#f5eef8" },
    "Thẻ ngân hàng": { icon: "💳", color: "#E07B39", bg: "#fef5ec" },
    "Thu công nợ":   { icon: "📋", color: "#2D4A22", bg: "#eafaf1" },
    "Khác":          { icon: "💰", color: "#7F8C8D", bg: "#f2f3f4" },
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const win = window.open("", "_blank", "width=420,height=600");
    win.document.write(`
      <html><head><title>Thống kê thu</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; color: #000; }
        h2 { text-align: center; font-size: 16px; margin-bottom: 4px; }
        .sub { text-align: center; color: #666; font-size: 11px; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f4f4f4; padding: 8px 10px; text-align: left; font-size: 11px; text-transform: uppercase; }
        td { padding: 10px; border-bottom: 1px solid #eee; }
        .amount { text-align: right; font-weight: bold; font-family: monospace; }
        .pct { text-align: right; color: #888; font-size: 11px; }
        .total-row td { font-weight: bold; background: #f9f9f9; border-top: 2px solid #ddd; }
        .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #aaa; }
      </style></head><body>
      ${printContent.innerHTML}
      <div class="footer">In lúc: ${new Date().toLocaleString("vi-VN")}</div>
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const entries = Object.entries(methods).sort((a, b) => b[1] - a[1]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-white font-bold text-base">Chi tiết Tổng Thu</div>
            <div className="text-white/80 text-xs mt-0.5">Theo hình thức thanh toán</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* Tổng cộng */}
        <div className="bg-emerald-50 px-5 py-3 flex justify-between items-center border-b border-emerald-100">
          <span className="text-sm font-semibold text-emerald-800">Tổng Thu</span>
          <span className="font-mono font-bold text-emerald-700 text-lg">{formatVND(total)}</span>
        </div>

        {/* Nội dung in ẩn */}
        <div ref={printRef} style={{ display: "none" }}>
          <h2>Thống Kê Thu Theo HTTT</h2>
          <div className="sub">Tổng: {formatVND(total)}</div>
          <table>
            <thead><tr><th>Hình thức</th><th style={{textAlign:"right"}}>Số tiền</th><th style={{textAlign:"right"}}>Tỷ lệ</th></tr></thead>
            <tbody>
              {entries.map(([label, amount]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td className="amount">{formatVND(amount)}</td>
                  <td className="pct">{total > 0 ? ((amount/total)*100).toFixed(1) : 0}%</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>TỔNG CỘNG</td>
                <td className="amount">{formatVND(total)}</td>
                <td className="pct">100%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Danh sách */}
        <div className="px-5 py-4 space-y-2.5 max-h-72 overflow-y-auto">
          {entries.length === 0 ? (
            <div className="text-center text-ink-muted text-sm py-6">Chưa có dữ liệu</div>
          ) : entries.map(([label, amount]) => {
            const cfg = METHOD_CONFIG[label] || METHOD_CONFIG["Khác"];
            const pct = total > 0 ? (amount / total) * 100 : 0;
            return (
              <div key={label} className="rounded-xl p-3.5 flex items-center gap-3"
                style={{ background: cfg.bg }}>
                <div className="text-2xl w-9 text-center">{cfg.icon}</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold" style={{ color: cfg.color }}>{label}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-black/10 overflow-hidden">
                    <div className="h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: cfg.color }} />
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-sm" style={{ color: cfg.color }}>{formatVND(amount)}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5">{pct.toFixed(1)}%</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex gap-2">
          <button onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors">
            🖨️ In thống kê
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-border text-ink-muted hover:bg-cream text-sm transition-colors">
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Modal chi tiết cho Chi / Lãi Gộp / Lãi Thuần / Lãi Ròng ─────────────────
function FinanceDetailModal({ open, type, onClose, data }) {
  const printRef = React.useRef();
  if (!open || !data) return null;

  const CONFIGS = {
    expense: {
      title: "Chi tiết Tổng Chi",
      sub: "Theo danh mục chi phí",
      color: "#C0392B", bg: "from-red-600 to-rose-500", headerBg: "#fef2f2", headerText: "#991b1b",
      printTitle: "Thống Kê Chi Phí",
    },
    gross: {
      title: "Chi tiết Lãi Gộp",
      sub: "Doanh thu − Giá vốn hàng bán",
      color: "#7c3aed", bg: "from-[#7c3aed] to-[#a855f7]", headerBg: "#f5f3ff", headerText: "#5b21b6",
      printTitle: "Chi Tiết Lãi Gộp",
    },
    net: {
      title: "Chi tiết Lãi Thuần",
      sub: "Lãi Gộp − Chi phí hoạt động",
      color: "#0369a1", bg: "from-sky-600 to-blue-500", headerBg: "#f0f9ff", headerText: "#075985",
      printTitle: "Chi Tiết Lãi Thuần",
    },
    pure: {
      title: "Chi tiết Lãi Ròng",
      sub: "Lãi Thuần − Thuế & lãi ngân hàng",
      color: "#b45309", bg: "from-amber-500 to-orange-400", headerBg: "#fffbeb", headerText: "#92400e",
      printTitle: "Chi Tiết Lãi Ròng",
    },
  };

  const cfg = CONFIGS[type] || CONFIGS.expense;

  // Tính rows theo loại
  const rows = (() => {
    if (type === "expense") {
      // Breakdown chi theo danh mục
      const breakdown = (data.category_breakdown || []).filter(c => c.type === "expense");
      const total = data.total_expense || 0;
      return {
        total,
        totalLabel: "Tổng Chi",
        items: breakdown.map(c => ({ label: c.category_name, amount: c.total, color: c.color, icon: "📌" })),
      };
    }
    if (type === "gross") {
      const revenue = data.total_revenue_orders ?? 0;
      const cogs = data.total_cogs ?? 0;
      const gross = data.gross_profit ?? 0;
      return {
        total: gross,
        totalLabel: "Lãi Gộp",
        items: [
          { label: "Doanh thu bán hàng", amount: revenue, color: "#16A085", icon: "💰", sign: "+" },
          { label: "Giá vốn hàng bán (COGS)", amount: -cogs, color: "#C0392B", icon: "📦", sign: "−" },
        ],
        formula: "Lãi Gộp = Doanh thu − COGS",
      };
    }
    if (type === "net") {
      const gross = data.gross_profit ?? 0;
      const expense = data.total_expense ?? 0;
      const net = data.net_profit ?? 0;
      return {
        total: net,
        totalLabel: "Lãi Thuần",
        items: [
          { label: "Lãi Gộp", amount: gross, color: "#7c3aed", icon: "📈", sign: "+" },
          { label: "Chi phí hoạt động", amount: -expense, color: "#C0392B", icon: "💸", sign: "−" },
        ],
        formula: "Lãi Thuần = Lãi Gộp − Chi phí hoạt động",
      };
    }
    if (type === "pure") {
      const net = data.net_profit ?? 0;
      const taxBank = data.tax_bank_expense ?? 0;
      const pure = data.pure_profit ?? 0;
      return {
        total: pure,
        totalLabel: "Lãi Ròng",
        items: [
          { label: "Lãi Thuần", amount: net, color: "#0369a1", icon: "📊", sign: "+" },
          { label: "Thuế & lãi ngân hàng", amount: -taxBank, color: "#C0392B", icon: "🏛️", sign: "−" },
        ],
        formula: "Lãi Ròng = Lãi Thuần − Thuế & lãi NH",
      };
    }
    return { total: 0, totalLabel: "", items: [] };
  })();

  const handlePrint = () => {
    const win = window.open("", "_blank", "width=460,height=600");
    win.document.write(`
      <html><head><title>${cfg.printTitle}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; font-size: 13px; color: #000; }
        h2 { text-align:center; font-size:16px; margin-bottom:4px; }
        .sub { text-align:center; color:#666; font-size:11px; margin-bottom:16px; }
        table { width:100%; border-collapse:collapse; margin-top:12px; }
        th { background:#f4f4f4; padding:8px 10px; text-align:left; font-size:11px; text-transform:uppercase; }
        td { padding:10px; border-bottom:1px solid #eee; }
        .right { text-align:right; font-weight:bold; font-family:monospace; }
        .total-row td { font-weight:bold; background:#f9f9f9; border-top:2px solid #ddd; }
        .formula { text-align:center; font-size:11px; color:#888; margin-top:12px; font-style:italic; }
        .footer { text-align:center; margin-top:20px; font-size:10px; color:#aaa; }
        .pos { color: #16a085; } .neg { color: #c0392b; }
      </style></head><body>
      <h2>${cfg.printTitle}</h2>
      <div class="sub">${cfg.sub}</div>
      <table>
        <thead><tr><th>Khoản mục</th><th style="text-align:right">Số tiền</th></tr></thead>
        <tbody>
          ${rows.items.map(it => `
            <tr>
              <td>${it.icon || ""} ${it.label}</td>
              <td class="right ${it.amount >= 0 ? 'pos' : 'neg'}">${it.sign || ""}${formatVND(Math.abs(it.amount))}</td>
            </tr>`).join("")}
          <tr class="total-row">
            <td>${rows.totalLabel}</td>
            <td class="right ${rows.total >= 0 ? 'pos' : 'neg'}">${formatVND(rows.total)}</td>
          </tr>
        </tbody>
      </table>
      ${rows.formula ? `<div class="formula">${rows.formula}</div>` : ""}
      <div class="footer">In lúc: ${new Date().toLocaleString("vi-VN")}</div>
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const isPositive = rows.total >= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header gradient */}
        <div className={`bg-gradient-to-r ${cfg.bg} px-5 py-4 flex items-center justify-between`}>
          <div>
            <div className="text-white font-bold text-base">{cfg.title}</div>
            <div className="text-white/80 text-xs mt-0.5">{cfg.sub}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none">✕</button>
        </div>

        {/* Tổng */}
        <div className="px-5 py-3 flex justify-between items-center border-b border-border"
          style={{ background: cfg.headerBg }}>
          <span className="text-sm font-semibold" style={{ color: cfg.headerText }}>{rows.totalLabel}</span>
          <span className="font-mono font-bold text-lg" style={{ color: isPositive ? cfg.color : "#C0392B" }}>
            {formatVND(rows.total)}
          </span>
        </div>

        {/* Formula badge */}
        {rows.formula && (
          <div className="mx-5 mt-3 px-3 py-1.5 rounded-lg bg-gray-50 border border-border text-center text-xs text-ink-muted italic">
            {rows.formula}
          </div>
        )}

        {/* Rows */}
        <div className="px-5 py-4 space-y-2.5 max-h-72 overflow-y-auto">
          {rows.items.length === 0 ? (
            <div className="text-center text-ink-muted text-sm py-6">Chưa có dữ liệu</div>
          ) : rows.items.map((it, idx) => {
            const isNeg = it.amount < 0;
            const absAmt = Math.abs(it.amount);
            const maxAmt = Math.max(...rows.items.map(x => Math.abs(x.amount)), 1);
            const pct = (absAmt / maxAmt) * 100;
            return (
              <div key={idx} className="rounded-xl p-3.5 flex items-center gap-3 bg-gray-50 border border-border/50">
                <div className="text-2xl w-9 text-center">{it.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink truncate">{it.label}</div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-black/10 overflow-hidden">
                    <div className="h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: it.color || cfg.color }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-mono font-bold text-sm" style={{ color: isNeg ? "#C0392B" : (it.color || cfg.color) }}>
                    {it.sign || ""}{formatVND(absAmt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex gap-2">
          <button onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors"
            style={{ background: cfg.color }}>
            🖨️ In thống kê
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-border text-ink-muted hover:bg-cream text-sm transition-colors">
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Finance() {
  // Period state
  const [period, setPeriod]     = useState("monthly");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [tab, setTab]           = useState("overview"); // "overview" | "list" | "categories"

  // Modals
  const [txnModal, setTxnModal]       = useState(null); // null | "income" | "expense"
  const [catModal, setCatModal]       = useState(false);
  const [editCat, setEditCat]         = useState(null);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [detailModal, setDetailModal]         = useState(null); // null | 'expense'|'gross'|'net'|'pure'

  const { data, loading, reload } = useFinanceData(
    dateFrom && dateTo ? null : period,
    dateFrom, dateTo
  );
  const { cats, reload: reloadCats } = useCategories();

  const incomeBreakdown  = useMemo(() => (data?.category_breakdown || []).filter(c => c.type === "income"), [data]);
  const expenseBreakdown = useMemo(() => (data?.category_breakdown || []).filter(c => c.type === "expense"), [data]);

  const handleDeleteTxn = async (id) => {
    if (!window.confirm("Xoá giao dịch này?")) return;
    await api.delete(`/finance/transactions/${id}`);
    toast({ title: "Đã xoá giao dịch", variant: "success" });
    reload();
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm("Xoá danh mục này?")) return;
    await api.delete(`/finance/categories/${id}`);
    toast({ title: "Đã xoá danh mục", variant: "success" });
    reloadCats();
  };

  const resetDate = () => { setDateFrom(""); setDateTo(""); };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Thu Chi & Lãi Gộp</h1>
          <p className="text-sm text-ink-secondary mt-1">Doanh thu · Giá vốn · Lãi gộp · Lãi thuần · Lãi ròng</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTxnModal("income")}>
            <TrendingUp size={14} className="text-emerald-600" /> Ghi Thu
          </Button>
          <Button variant="primary" onClick={() => setTxnModal("expense")}>
            <TrendingDown size={14} /> Ghi Chi
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-cream-dark/40 p-1 rounded-lg">
          {PERIOD_OPTS.map(p => (
            <button key={p.value}
              onClick={() => { setPeriod(p.value); resetDate(); }}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-semibold transition-all",
                !dateFrom && !dateTo && period === p.value
                  ? "bg-white shadow text-bamboo border border-border/40"
                  : "text-ink-muted hover:text-ink"
              )}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="!mb-0.5 text-[10px]">Từ ngày</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-36" />
          </div>
          <div>
            <Label className="!mb-0.5 text-[10px]">Đến ngày</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-36" />
          </div>
          {(dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" onClick={resetDate}><X size={12} /></Button>
          )}
        </div>
      </div>

      {/* Stats */}
      {data && (
        <>
          {/* Row 1: Thu / Chi / Lãi gộp */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="cursor-pointer" onClick={() => setShowIncomeModal(true)}>
              <StatCard label="Tổng Thu" value={data.total_income} icon={TrendingUp} themeKey="income"
                sub="Bấm để xem chi tiết" />
            </div>
            <div className="cursor-pointer" onClick={() => setDetailModal('expense')}>
              <StatCard label="Tổng Chi" value={data.total_expense} icon={TrendingDown} themeKey="expense" sub="Bấm để xem chi tiết" />
            </div>
            <div className="cursor-pointer" onClick={() => setDetailModal('gross')}>
              <StatCard
                label="Lãi Gộp"
                value={data.gross_profit ?? 0}
                icon={DollarSign}
                themeKey={(data.gross_profit ?? 0) >= 0 ? "gross_profit" : "profit_negative"}
                sub="Bấm để xem chi tiết"
              />
            </div>
          </div>

          {/* Row 2: Lãi thuần / Lãi ròng */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="cursor-pointer" onClick={() => setDetailModal('net')}>
              <StatCard
                label="Lãi Thuần"
                value={data.net_profit ?? 0}
                icon={TrendingUp}
                themeKey={(data.net_profit ?? 0) >= 0 ? "net_profit" : "profit_negative"}
                sub="Bấm để xem chi tiết"
              />
            </div>
            <div className="cursor-pointer" onClick={() => setDetailModal('pure')}>
              <StatCard
                label="Lãi Ròng"
                value={data.pure_profit ?? 0}
                icon={DollarSign}
                themeKey={(data.pure_profit ?? 0) >= 0 ? "pure_profit" : "profit_negative"}
                sub="Bấm để xem chi tiết"
              />
            </div>
          </div>

          {/* Bảng tóm tắt P&L */}
          <div className="rounded-xl border border-border bg-white overflow-hidden">
            <div className="px-4 py-3 bg-cream/40 border-b border-border">
              <span className="text-sm font-semibold text-ink">Kết quả kinh doanh (P&L)</span>
            </div>
            <div className="divide-y divide-border text-sm">
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-ink-muted">Doanh thu bán hàng</span>
                <span className="font-mono font-semibold text-emerald-700">+{formatVND(data.total_revenue_orders ?? 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-ink-muted">Giá vốn hàng bán (COGS)</span>
                <span className="font-mono text-red-600">−{formatVND(data.total_cogs ?? 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5 bg-violet-50">
                <span className="font-semibold text-violet-700">Lãi Gộp</span>
                <span className={cn("font-mono font-bold", (data.gross_profit ?? 0) >= 0 ? "text-violet-700" : "text-red-700")}>{formatVND(data.gross_profit ?? 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-ink-muted">Chi phí hoạt động</span>
                <span className="font-mono text-red-600">−{formatVND(data.total_expense ?? 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5 bg-sky-50">
                <span className="font-semibold text-sky-700">Lãi Thuần</span>
                <span className={cn("font-mono font-bold", (data.net_profit ?? 0) >= 0 ? "text-sky-700" : "text-red-700")}>{formatVND(data.net_profit ?? 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-ink-muted">Thuế & lãi ngân hàng</span>
                <span className="font-mono text-red-600">−{formatVND(data.tax_bank_expense ?? 0)}</span>
              </div>
              <div className="flex justify-between px-4 py-3 bg-amber-50">
                <span className="font-bold text-amber-700">Lãi Ròng</span>
                <span className={cn("font-mono font-bold text-lg", (data.pure_profit ?? 0) >= 0 ? "text-amber-700" : "text-red-700")}>{formatVND(data.pure_profit ?? 0)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {[
          { id: "overview", label: "Biểu đồ", icon: BarChart2 },
          { id: "list",     label: "Danh sách giao dịch", icon: List },
          { id: "categories", label: "Danh mục", icon: Tag },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px",
              tab === id ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"
            )}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Tab: Biểu đồ tròn */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-emerald-700">Cơ cấu Thu</CardTitle></CardHeader>
            <CardContent>
              <BreakdownChart data={incomeBreakdown} title="Phân bổ nguồn thu" emptyText="Chưa có dữ liệu thu" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-red-600">Cơ cấu Chi</CardTitle></CardHeader>
            <CardContent>
              <BreakdownChart data={expenseBreakdown} title="Phân bổ khoản chi" emptyText="Chưa có dữ liệu chi" />
            </CardContent>
          </Card>

          {/* Summary table by category */}
          {data?.category_breakdown?.length > 0 && (
            <Card className="md:col-span-2">
              <CardHeader><CardTitle>Chi tiết theo danh mục</CardTitle></CardHeader>
              <CardContent className="!p-0">
                <table className="w-full text-sm">
                  <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                    <tr>
                      <th className="text-left py-3 px-4">Danh mục</th>
                      <th className="text-left py-3 px-4">Loại</th>
                      <th className="text-right py-3 px-4">Số GD</th>
                      <th className="text-right py-3 px-4">Tổng tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.category_breakdown
                      .sort((a, b) => b.total - a.total)
                      .map((c, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="py-3 px-4">
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                              {c.category_name}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={c.type === "income" ? "delivered" : "cancelled"}>
                              {c.type === "income" ? "Thu" : "Chi"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right text-ink-muted">{c.count}</td>
                          <td className={cn("py-3 px-4 text-right font-mono font-semibold", c.type === "income" ? "text-emerald-700" : "text-red-600")}>
                            {formatVND(c.total)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tab: Danh sách giao dịch */}
      {tab === "list" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Giao dịch</CardTitle>
              <span className="text-sm text-ink-muted">{data?.transactions?.length || 0} giao dịch</span>
            </div>
          </CardHeader>
          <CardContent className="!p-0">
            {loading ? (
              <table className="w-full text-sm"><tbody><FinanceRowsSkeleton rows={6} /></tbody></table>
            ) : !data?.transactions?.length ? (
              <div className="py-12 text-center text-ink-muted text-sm">Chưa có giao dịch nào</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="text-left py-3 px-4">Ngày</th>
                    <th className="text-left py-3 px-4">Loại</th>
                    <th className="text-left py-3 px-4">Danh mục</th>
                    <th className="text-left py-3 px-4">Diễn giải</th>
                    <th className="text-right py-3 px-4">Số tiền</th>
                    <th className="text-right py-3 px-4">Nguồn</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((txn) => (
                    <tr key={txn.transaction_id} className="border-t border-border hover:bg-cream/30">
                      <td className="py-3 px-4 text-xs text-ink-muted whitespace-nowrap">
                        {formatDate(txn.date)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={txn.type === "income" ? "delivered" : "cancelled"}>
                          {txn.type === "income" ? "↑ Thu" : "↓ Chi"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: txn.category_color || "#6B7280" }} />
                          {txn.category_name}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-ink-secondary text-xs">{txn.description || "—"}</td>
                      <td className={cn("py-3 px-4 text-right font-mono font-semibold", txn.type === "income" ? "text-emerald-700" : "text-red-600")}>
                        {txn.type === "income" ? "+" : "-"}{formatVND(txn.amount)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-[10px] text-ink-muted">
                          {txn.source === "debt_collect" ? "Công nợ" : "Thủ công"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => handleDeleteTxn(txn.transaction_id)}
                          className="p-1 text-ink-muted hover:text-red-600 rounded transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Danh mục */}
      {tab === "categories" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => { setEditCat(null); setCatModal(true); }}>
              <PlusCircle size={14} /> Thêm danh mục
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["expense", "income"].map(catType => (
              <Card key={catType}>
                <CardHeader>
                  <CardTitle className={catType === "income" ? "text-emerald-700" : "text-red-600"}>
                    {catType === "income" ? "Danh mục Thu" : "Danh mục Chi"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="!p-0">
                  {cats.filter(c => c.type === catType).length === 0 ? (
                    <p className="text-sm text-ink-muted text-center py-8">Chưa có danh mục</p>
                  ) : (
                    <ul>
                      {cats.filter(c => c.type === catType).map(cat => (
                        <li key={cat.category_id} className="flex items-center justify-between px-4 py-3 border-t border-border first:border-t-0 hover:bg-cream/30">
                          <span className="flex items-center gap-2.5">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                            <span className="text-sm font-medium">{cat.name}</span>
                            {cat.description && <span className="text-xs text-ink-muted">— {cat.description}</span>}
                          </span>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditCat(cat); setCatModal(true); }}
                              className="p-1.5 text-ink-muted hover:text-bamboo rounded transition-colors">
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => handleDeleteCat(cat.category_id)}
                              className="p-1.5 text-ink-muted hover:text-red-600 rounded transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <TransactionModal
        open={!!txnModal}
        onClose={() => setTxnModal(null)}
        onSaved={reload}
        cats={cats}
        defaultType={txnModal || "expense"}
      />
      <CategoryModal
        open={catModal}
        onClose={() => { setCatModal(false); setEditCat(null); }}
        onSaved={reloadCats}
        editItem={editCat}
      />
      <IncomeDetailModal
        open={showIncomeModal}
        onClose={() => setShowIncomeModal(false)}
        data={data}
      />
      <FinanceDetailModal
        open={!!detailModal}
        type={detailModal}
        onClose={() => setDetailModal(null)}
        data={data}
      />
    </div>
  );
}